import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Popover,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import { Tune as TuneIcon } from '@mui/icons-material';
import React, { ReactElement, useState } from 'react';
import {
  SearchFieldConfig,
  searchFieldInputType,
  searchMatchMode,
  SearchMatchMode,
} from '../../../types/searchCustomize';
import { gray40, gray60, gray80, white, orange50 } from '../../../constants/colors';

interface SearchCustomizePanelProps {
  fields: SearchFieldConfig[];
  onUpdateField: (fieldName: string, updates: Partial<SearchFieldConfig>) => void;
  onReset: () => void;
  onSave?: () => void;
  isSaving?: boolean;
}

const matchModeLabels: Record<SearchMatchMode, string> = {
  [searchMatchMode.fuzzy]: 'あいまい',
  [searchMatchMode.exact]: '完全一致',
};

/** matchModeセレクトを表示する入力タイプかどうか
 *  - text: あいまい/完全一致をユーザーが選べる
 *  - date: あいまい(TextField入力)/完全一致(Datepicker)で UI を出し分けるために必要
 *  - number: カンマ表示と素値の差を吸収するため完全一致を選べるようにする
 *  - choice(選択肢項目): 完全一致固定のため非表示にする（#3204）。
 *    設定済みの選択肢から選ぶ以上あいまい検索は不要で、部分一致だと出力値 "1" が "10" にヒットする
 */
const showMatchModeSelect = (inputType: string): boolean =>
  inputType === searchFieldInputType.text ||
  inputType === searchFieldInputType.date ||
  inputType === searchFieldInputType.number;

const cellSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const SearchCustomizePanel = (props: SearchCustomizePanelProps): ReactElement => {
  const { fields, onUpdateField, onReset, onSave, isSaving } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const fixedFields = fields.filter(f => !f.isOcrItem);
  const ocrFields = fields.filter(f => f.isOcrItem);

  const renderFixedFieldRow = (field: SearchFieldConfig): ReactElement => (
    <Box
      key={field.fieldName}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
    >
      {field.canToggleList ? (
        // 作成者など: 検索ドロップダウンと一覧列の表示を1つのトグルで連動させる（デフォルト非表示）
        <FormControlLabel
          control={
            <Checkbox
              checked={field.showInList ?? false}
              onChange={(e): void =>
                onUpdateField(field.fieldName, {
                  showInList: e.target.checked,
                  enabled: e.target.checked,
                })
              }
              size="small"
              sx={{ '&.Mui-checked': { color: gray80 }, padding: '4px' }}
            />
          }
          label={<Typography sx={{ fontSize: '13px' }}>{field.label}</Typography>}
          sx={{ margin: 0, flex: 1 }}
        />
      ) : (
        <FormControlLabel
          control={
            <Checkbox
              checked={field.enabled}
              onChange={(e): void => onUpdateField(field.fieldName, { enabled: e.target.checked })}
              size="small"
              sx={{ '&.Mui-checked': { color: gray80 }, padding: '4px' }}
            />
          }
          label={<Typography sx={{ fontSize: '13px' }}>{field.label}</Typography>}
          sx={{ margin: 0, flex: 1 }}
        />
      )}
      {!field.canToggleList && showMatchModeSelect(field.inputType) && (
        <Select
          value={field.matchMode}
          onChange={(e): void =>
            onUpdateField(field.fieldName, { matchMode: e.target.value as SearchMatchMode })
          }
          size="small"
          disabled={!field.enabled}
          sx={{
            fontSize: '12px',
            minWidth: '100px',
            height: '28px',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: gray40 },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: gray60 },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: gray60,
              borderWidth: '1px',
            },
          }}
        >
          {Object.entries(searchMatchMode).map(([key, value]) => (
            <MenuItem key={key} value={value} sx={{ fontSize: '12px' }}>
              {matchModeLabels[value]}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        sx={{
          border: `1px solid ${gray80}`,
          borderRadius: '4px',
          padding: '6px 12px',
          gap: '4px',
          backgroundColor: white,
          color: gray80,
          height: '30px',
          '&:hover': {
            borderColor: gray60,
            color: gray60,
            backgroundColor: white,
            opacity: '80%',
          },
        }}
      >
        <TuneIcon sx={{ fontSize: '16px' }} />
        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>検索・表示設定</Typography>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              marginTop: '4px',
              minWidth: '440px',
              // ビューポート幅を超えないようガード（特にSPで重要）
              maxWidth: 'calc(100vw - 32px)',
              // ビューポート高さに応じた最大高さ（#2642 担当者フィールド追加で640px固定では収まらない環境がある）
              maxHeight: 'min(640px, calc(100vh - 96px))',
              borderRadius: '8px',
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              '@media screen and (max-width: 600px)': {
                // SPではminWidthを解除してmaxWidthに収まるようにする
                minWidth: 0,
              },
            },
          },
        }}
      >
        {/* スクロール可能なコンテンツ領域 */}
        <Box sx={{ flex: 1, overflowY: 'auto', padding: '20px', paddingBottom: '8px' }}>
          {/* 固定フィールド（検索のみ） */}
          {fixedFields.length > 0 && (
            <>
              <Typography
                sx={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: gray80 }}
              >
                固定項目
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {fixedFields.map(renderFixedFieldRow)}
              </Box>
            </>
          )}

          {/* OCR読取項目：テーブル形式 */}
          {ocrFields.length > 0 && (
            <>
              {fixedFields.length > 0 && <Divider sx={{ marginY: '12px' }} />}
              <Typography
                sx={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: gray80 }}
              >
                読取項目
              </Typography>

              {/* テーブルヘッダー */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 72px 72px',
                  alignItems: 'center',
                  paddingX: '4px',
                  paddingBottom: '4px',
                  borderBottom: `1px solid ${gray40}`,
                }}
              >
                <Typography sx={{ fontSize: '11px', color: gray60 }}>項目名</Typography>
                <Typography sx={{ fontSize: '11px', color: gray60, textAlign: 'center' }}>
                  検索
                </Typography>
                <Typography sx={{ fontSize: '11px', color: gray60, textAlign: 'center' }}>
                  一覧表示
                </Typography>
              </Box>

              {/* テーブル行 */}
              {ocrFields.map((field, index) => (
                <Box
                  key={field.fieldName}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 72px 72px',
                    alignItems: 'center',
                    paddingX: '4px',
                    paddingY: '2px',
                    backgroundColor: index % 2 === 0 ? white : '#fafafa',
                  }}
                >
                  <Typography sx={{ fontSize: '13px' }}>{field.label}</Typography>
                  <Box sx={cellSx}>
                    <Checkbox
                      checked={field.enabled}
                      onChange={(e): void => {
                        const enabled = e.target.checked;
                        onUpdateField(field.fieldName, {
                          enabled,
                          ...(enabled ? { showInList: true } : {}),
                        });
                      }}
                      size="small"
                      sx={{ '&.Mui-checked': { color: gray80 }, padding: '4px' }}
                    />
                  </Box>
                  <Box sx={cellSx}>
                    <Checkbox
                      checked={field.showInList ?? false}
                      onChange={(e): void =>
                        onUpdateField(field.fieldName, { showInList: e.target.checked })
                      }
                      size="small"
                      sx={{ '&.Mui-checked': { color: gray80 }, padding: '4px' }}
                    />
                  </Box>
                </Box>
              ))}

              {/* あいまい/完全一致セレクト（検索ONの項目のみ） */}
              {ocrFields.some(f => f.enabled && showMatchModeSelect(f.inputType)) && (
                <>
                  <Divider sx={{ marginTop: '12px', marginBottom: '8px' }} />
                  <Typography sx={{ fontSize: '11px', color: gray60, marginBottom: '8px' }}>
                    検索方式
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {ocrFields
                      .filter(f => f.enabled && showMatchModeSelect(f.inputType))
                      .map(field => (
                        <Box
                          key={field.fieldName}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                          }}
                        >
                          <Typography sx={{ fontSize: '13px', flex: 1 }}>{field.label}</Typography>
                          <Select
                            value={field.matchMode}
                            onChange={(e): void =>
                              onUpdateField(field.fieldName, {
                                matchMode: e.target.value as SearchMatchMode,
                              })
                            }
                            size="small"
                            sx={{
                              fontSize: '12px',
                              minWidth: '100px',
                              height: '28px',
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: gray40 },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: gray60 },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: gray60,
                                borderWidth: '1px',
                              },
                            }}
                          >
                            {Object.entries(searchMatchMode).map(([key, value]) => (
                              <MenuItem key={key} value={value} sx={{ fontSize: '12px' }}>
                                {matchModeLabels[value]}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                      ))}
                  </Box>
                </>
              )}
            </>
          )}
        </Box>

        {/* 固定フッター：スクロールしても常に表示 */}
        <Box
          sx={{
            padding: '12px 20px',
            borderTop: `1px solid ${gray40}`,
            backgroundColor: white,
          }}
        >
          <Typography
            sx={{ fontSize: '11px', color: gray60, textAlign: 'right', marginBottom: '8px' }}
          >
            ※保存すると会社内の担当者全員に適用されます
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button
              onClick={onReset}
              size="small"
              sx={{
                fontSize: '12px',
                color: gray80,
                backgroundColor: white,
                border: `1px solid ${gray60}`,
                '&:hover': { backgroundColor: white, borderColor: gray80, opacity: '80%' },
                boxShadow: 'none',
                padding: '4px 12px',
              }}
            >
              初期設定に戻す
            </Button>
            <Button
              onClick={async (): Promise<void> => {
                if (!onSave) return;
                await onSave();
                handleClose();
              }}
              disabled={isSaving || !onSave}
              size="small"
              sx={{
                fontSize: '12px',
                color: white,
                backgroundColor: orange50,
                border: `1px solid ${orange50}`,
                '&:hover': { backgroundColor: orange50, opacity: '80%' },
                '&.Mui-disabled': {
                  backgroundColor: gray40,
                  borderColor: gray40,
                  color: white,
                },
                boxShadow: 'none',
                padding: '4px 12px',
              }}
            >
              {isSaving ? '保存中...' : 'この設定を保存する'}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default SearchCustomizePanel;
